import { NgApiClient } from '@src/api/ngApiClient';
import { IMediaFile, TApiEntity, UploadMediaResponse } from '@src/models/api';
import { AnyType } from '@src/models/common';

export interface IUploaderFile extends IMediaFile {
  loading: boolean;
  loadingError: boolean;
  uuid: string;
  fileSize: number;
  originalName: string;
  originalFile: AnyType;
}

/** Ключ - имя файла, значение массив ошибок, так потому что валидация происходит по файлу */
export type TErrorsMap = Map<string, string[]>;

type TFileListenerParams = { listenerName: string; cb: (v: IUploaderFile[], isExternal?: boolean) => void };
type TErrorListenerParams = { listenerName: string; cb: (e: TErrorsMap) => void };

/**
 * - Предоставляет расширенный функционал для контроля загрузчика
 * - Позволяет синхронизировать разные загрузчики
 */
export class UploaderController {
  /** Текущий список файлов */
  public fileList: IUploaderFile[] = [];

  /** Текущий список ошибок */
  public errorsMap: TErrorsMap = new Map();

  private errorListListeners = new Map<string, (e: TErrorsMap) => void>();

  private fileListListeners = new Map<string, (v: IUploaderFile[], isExternal?: boolean) => void>();

  /** Ключ - имя файла, значение - статус загрузки */
  private fileLoadingList = new Map<string, boolean>();

  constructor({ fileList = [] }: { fileList?: IUploaderFile[] | IUploaderFile } = { fileList: [] }) {
    if (Array.isArray(fileList)) {
      this.fileList = fileList;
    } else {
      this.fileList = [fileList];
    }
  }

  /**
   * Добавить слушателя при обновлении списка файлов.
   *
   * **Attention!** Убедитесь, что имя слушателя уникально!
   */
  public addFileListener = (listeners: TFileListenerParams | TFileListenerParams[]) => {
    if (Array.isArray(listeners)) {
      listeners.forEach(({ listenerName, cb }) => {
        this.fileListListeners.set(listenerName, cb);
      });
    } else {
      this.fileListListeners.set(listeners.listenerName, listeners.cb);
    }
  };

  /** Оповещаем всех слушателей о изменении files */
  private notifyFilesListeners = (isExternal?: boolean) => {
    Array.from(this.fileListListeners).forEach(([, callback]) => callback(this.fileList, isExternal));
  };

  /** Оповещаем всех слушателей о изменении errors */
  private notifyErrorsListeners = () => {
    Array.from(this.errorListListeners).forEach(([, callback]) => callback(this.errorsMap));
  };

  /** Добавление файла или массива файлов */
  public addFile = (v: IUploaderFile[], isMulti?: boolean) => {
    Array.isArray(v) ? this.fileList.push(...v) : this.fileList.push(v);
    if (isMulti) {
      /** Важно менять ссылку т.к. на нее подписаны реакт компоненты */
      this.fileList = [...this.fileList];
    } else {
      this.fileList = [this.fileList[this.fileList.length - 1]];
    }

    this.notifyFilesListeners();
  };

  /** Мутируем стейт, чтобы подтянуть данные, пришедшие с бека */
  public mutateFile = (v: UploadMediaResponse | null, uuid: string, isMulti?: boolean, loadingError?: boolean) => {
    const mutatedArr = this.fileList.map((el) => {
      if (el.uuid === uuid) {
        el.loading = false;
        if (loadingError || !v) {
          return { ...el, loadingError: true };
        }
        el.loadingError = false;
        return {
          ...el,
          ...v,
        };
      } else {
        return el;
      }
    });

    this.fileList = [...(mutatedArr as IUploaderFile[])];

    this.notifyFilesListeners();
  };

  /** Полное изменение файлов
   * isExternal нужен для того, чтобы внешние изменения value не триггерили onChange
   */
  public setStateFiles = (v: IUploaderFile[], isExternal?: boolean) => {
    this.fileList = v;
    this.notifyFilesListeners(isExternal);
  };

  /**
   * Добавить слушателя при обновлении списка ошибок.
   *
   * **Attention!** Убедитесь, что имя слушателя уникально!
   */
  public addErrorListener = (listeners: TErrorListenerParams | TErrorListenerParams[]) => {
    if (Array.isArray(listeners)) {
      listeners.forEach(({ listenerName, cb }) => {
        this.errorListListeners.set(listenerName, cb);
      });
    } else {
      this.errorListListeners.set(listeners.listenerName, listeners.cb);
    }
  };

  public setStateErrors = (errorsMap: TErrorsMap) => {
    this.errorsMap = new Map(errorsMap);
    this.notifyErrorsListeners();
  };

  public uploadFiles = async ({
    ngApiClient,
    file,
    entityId,
    entityFieldId,
    isRetry,
    isExternal,
    isMulti,
  }: {
    ngApiClient: NgApiClient;
    file?: AnyType;
    entityId?: TApiEntity;
    entityFieldId?: string;
    isRetry?: boolean;
    isExternal?: boolean;
    isMulti?: boolean;
  }) => {
    if (!entityId || !entityFieldId) return;
    const onlyNewFiles = this.fileList.filter((f) => f.originalFile);
    if (isExternal) {
      onlyNewFiles.map(async (f) => {
        try {
          const { data } = await ngApiClient.uploadMedia(entityId, entityFieldId, {
            mediaFile: f.originalFile,
          });
          this.mutateFile(data, f.uuid, isMulti);
          return data;
        } catch (error) {
          f.loadingError = true;
        }
      });
    } else {
      try {
        const { data } = await ngApiClient.uploadMedia(entityId, entityFieldId, {
          mediaFile: isRetry ? file.originalFile : file,
        });
        this.mutateFile(data, file.uuid, isMulti);
        return data;
      } catch (error) {
        if (!isMulti) {
          this.fileList[0].loadingError = true;
        } else {
          file.loadingError = true;
        }
      }
    }
  };

  public setFileErrors = ({ fileName, errors }: { fileName: string; errors: string[] }) => {
    const newErrorMap = new Map<string, string[]>();

    /** Сначала зачищаем старую мапу от текущего файла */
    Array.from(this.errorsMap).forEach(([errorCode, fileList]) => {
      const newFileList = fileList.filter((f) => f !== fileName);
      if (newFileList.length > 0) {
        newErrorMap.set(errorCode, newFileList);
      }
    });

    errors.forEach((e) => {
      const prevFileList = newErrorMap.get(e);
      if (prevFileList) {
        prevFileList.push(fileName);
        newErrorMap.set(e, prevFileList);
      } else {
        newErrorMap.set(e, [fileName]);
      }
    });

    this.errorsMap = newErrorMap;
    this.notifyErrorsListeners();
  };

  public setFileLoading = ({ fileName, loading }: { fileName: string; loading: boolean }) => {
    this.fileLoadingList.set(fileName, loading);
  };

  /** Зачистка всех состояний */
  public clear = () => {
    this.fileList = [];
    this.notifyFilesListeners();

    this.errorsMap = new Map();
    this.notifyErrorsListeners();

    this.fileLoadingList = new Map();
  };
}
